import { lazy, Suspense, useEffect, useState } from "react";

import { get2DImage, get3DImage } from "../../utils/common";

/**
 * Virtual Try-On dispatcher.
 *
 * Which experience ships is controlled by the `is3DVTO` prop (typically
 * `product.is_3D_VTO`):
 *
 *   is3DVTO truthy -> 3D try-on renderer
 *   otherwise      -> classic 2D try-on (DEFAULT)
 *
 * The mode is not frozen for the lifetime of the modal. Every similar-product
 * card reports its product back through `onSelectSimilarProduct`; if that
 * product's VTO mode differs from the one currently open, the dispatcher swaps
 * to the other implementation and re-initialises it with the newly selected
 * product (2D image / glb model), so switching 2D -> 3D and 3D -> 2D works in
 * both directions.
 *
 * Each implementation is loaded lazily so that only the selected variant's
 * JavaScript and stylesheet are pulled into the page. The two try-on
 * implementations share a number of CSS class names, so keeping them in
 * separate async chunks prevents their styles from clashing.
 */
const VirtualTryOn3D = lazy(() => import("./Virtual_try_on_3d"));
const VirtualTryOn2D = lazy(() => import("./Virtual_try_on_2d"));

const get2DImageUrl = (images) => {
  const image = get2DImage(images);
  return image && image !== "NA"
    ? process.env.REACT_APP_IMAGE_URL + image
    : "";
};

const get3DImageUrl = (images) => {
  const url = get3DImage(images);
  return url && url !== "NA" ? url : "";
};

/**
 * Decide whether a similar product should open in 3D or 2D mode.
 *
 * The `is_3D_VTO` flag is the product's declared type and mirrors how its own
 * PDP opens the try-on, so it wins: flag=true -> 3D, flag=false -> 2D
 * (regardless of extra assets). A product that declares 3D but ships no glb
 * degrades to 2D when a 2D image exists. Only when the flag is missing do we
 * infer the mode from the available assets, finally falling back to the
 * currently open mode.
 */
const resolveIs3D = (product, fallback) => {
  const has3D = !!get3DImageUrl(product?.Product_Images);
  const has2D = !!get2DImageUrl(product?.Product_Images);
  const flag =
    typeof product?.is_3D_VTO === "boolean" ? product.is_3D_VTO : null;

  if (flag === true && has3D) return true;
  if (flag === false) return false;
  if (flag === true) return has2D ? false : fallback;
  if (has3D && !has2D) return true;
  if (has2D && !has3D) return false;
  return fallback;
};

export default function VirtualTryOn({
  isOpen,
  is3DVTO,
  productImages,
  glbModal,
  size,
  total_measurements,
  product, 
  variant, 
  ...props
}) {
  const [is3D, setIs3D] = useState(() => Boolean(is3DVTO));
  // The similar product that caused the last mode switch (null = original
  // product props are still in use).
  const [switchedProduct, setSwitchedProduct] = useState(null);

  // Parents that keep this component mounted while the modal is closed (e.g.
  // the all-products page) would otherwise reopen with the mode/product of the
  // last similar-product selection instead of the product that was clicked.
  useEffect(() => {
    if (isOpen) return;
    setIs3D(Boolean(is3DVTO));
    setSwitchedProduct(null);
  }, [isOpen, is3DVTO]);

  const handleSelectSimilarProduct = (selected) => {
    if (!selected) return;

    const nextIs3D = resolveIs3D(selected, is3D);

    if (nextIs3D === is3D) return;

    setSwitchedProduct(selected);
    setIs3D(nextIs3D);
  };

  // Initial product data for the component that is about to mount after a
  // mode switch: each variant only consumes the asset it renders (2D uses
  // `productImages`, 3D uses `glbModal`), so both are derived from the
  // switched product.
  const activeProductImages = switchedProduct
    ? switchedProduct.Product_Images ?? productImages
    : productImages;
  const activeGlb = switchedProduct
    ? get3DImageUrl(switchedProduct.Product_Images) || glbModal
    : glbModal;
  const activeSize = switchedProduct?.size ?? size;
  const activeMeasurements =
    switchedProduct?.total_measurements ?? total_measurements;
  const activeProduct = switchedProduct || product;

  const VirtualTryOnComponent = is3D ? VirtualTryOn3D : VirtualTryOn2D;

  return (
    <Suspense fallback={null}>
      <VirtualTryOnComponent
        {...props}
        isOpen={isOpen}
        productImages={activeProductImages}
        glbModal={activeGlb}
        size={activeSize}
        total_measurements={activeMeasurements}
        product={activeProduct}
        variant={switchedProduct ? undefined : variant}
        onSelectSimilarProduct={handleSelectSimilarProduct}
      />
    </Suspense>
  );
}